import { matchesTokens } from "@/lib/search";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumber, formatIDR, todayISO, parseQty } from "@/lib/format";
import { Card, PageHeader, Badge, Modal, Button, Field, Input, Select, Textarea } from "@/components/ui-kit";
import { usePagination } from "@/hooks/usePagination";
import { FileText, ChevronLeft, ChevronRight, Truck, Plus, Trash2, Upload, Pencil, Download, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { DatePicker } from "@/components/DatePicker";

/**
 * ClientPos — Iter v27 #2.
 * Read-only PO & Rekap Kuota list for the logged-in klien, scoped to their client_name.
 * Each PO row exposes a detail modal showing per-item ordered vs delivered vs remaining qty.
 */
export default function ClientPos() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePo, setActivePo] = useState(null);

  // Iter v34v (#2) + v34x (#6/#7) — Klien dapat membuat PO sendiri. Kategori dihapus; satuan dropdown dari /masters/units.
  const blankItem = { material_name: "", unit: "", quantity: 1, unit_price: 0 };
  const blankForm = {
    po_number: "", contract_date: todayISO(),
    items: [{ ...blankItem }], notes: "",
    file_path: null, file_id: null, file_name: null, file_content_type: null,
  };
  const [openCreate, setOpenCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [units, setUnits] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/client/pos")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/masters/units").then((r) => setUnits(r.data || [])).catch(() => setUnits([]));
  }, []);

  const openNewPo = () => { setEditId(null); setForm({ ...blankForm, items: [{ ...blankItem }] }); setOpenCreate(true); };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...blankItem }] }));
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));
  const setItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, items });
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "po");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        file_path: r.data.path, file_id: r.data.id,
        file_name: r.data.filename, file_content_type: r.data.content_type,
      }));
      toast.success("File terunggah");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal upload");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.po_number || !form.items.length || !form.items[0].material_name) {
      toast.error("No. PO + minimal 1 item wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_name: user?.client_name || "",
        items: form.items.map((i) => ({
          ...i,
          quantity: parseQty(i.quantity),
          unit_price: parseQty(i.unit_price),
        })),
      };
      if (editId) {
        // Iter v34w (#1) — Edit oleh klien butuh approval admin (status → pending_edit / pending_approval).
        const r = await api.patch(`/client/pos/${editId}`, payload);
        if (r.data?.resubmitted) toast.success("PO diajukan ulang ke admin");
        else toast.success("Permintaan edit dikirim, menunggu persetujuan admin");
      } else {
        await api.post("/client/pos", payload);
        toast.success("PO berhasil dibuat — menunggu persetujuan admin");
      }
      setOpenCreate(false);
      setEditId(null);
      setForm(blankForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan PO");
    } finally {
      setSaving(false);
    }
  };

  // Iter v34w (#1) — Edit/Hapus/Ajukan Ulang oleh klien.
  const openEdit = (po) => {
    setEditId(po.id);
    setForm({
      po_number: po.po_number || "",
      contract_date: po.contract_date || todayISO(),
      items: (po.items || []).map((i) => ({ ...i })),
      notes: po.notes || "",
      file_path: po.file_path || null,
      file_id: po.file_id || null,
      file_name: po.file_name || null,
      file_content_type: po.file_content_type || null,
    });
    setOpenCreate(true);
  };
  const requestDelete = async (po) => {
    const msg = po.status === "pending_approval"
      ? `Batalkan PO ${po.po_number}? (belum di-approve admin)`
      : `Kirim permintaan hapus PO ${po.po_number} ke admin?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/client/pos/${po.id}`);
      toast.success(r.data?.deleted ? "PO dibatalkan" : "Permintaan hapus dikirim, menunggu persetujuan admin");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const STATUS_LABEL = {
    pending_approval: { txt: "Menunggu Persetujuan", tone: "warn" },
    pending_edit: { txt: "Edit Menunggu Approval", tone: "warn" },
    pending_delete: { txt: "Hapus Menunggu Approval", tone: "warn" },
    rejected: { txt: "Ditolak", tone: "danger" },
    open: { txt: "Aktif", tone: "emerald" },
  };

  // Iter v34bv (#11) — Search + Filter untuk Portal Klien.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [quotaFilter, setQuotaFilter] = useState("all"); // all | open | done

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((po) => {
      if (q) {
        // Iter v34cw (#5) — pencarian kombinasi kata.
        const hay = [
          po.po_number, po.notes, po.category, po.contract_date, po.status,
          ...(po.items || []).map((it) => it.material_name || ""),
        ].join(" ");
        if (!matchesTokens(hay, q)) return false;
      }
      if (statusFilter !== "all" && (po.status || "open") !== statusFilter) return false;
      if (quotaFilter !== "all") {
        const items = po.items || [];
        const ord = items.reduce((s, i) => s + Number(i.quantity || 0), 0);
        const del = items.reduce((s, i) => s + Number(i.delivered || 0), 0);
        const done = ord > 0 && del >= ord;
        if (quotaFilter === "done" && !done) return false;
        if (quotaFilter === "open" && done) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, quotaFilter]);

  const pagi = usePagination(filtered, { perPage: 14 });

  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
  };

  const computeQuotaStatus = (po) => {
    const items = po.items || [];
    if (items.length === 0) return { label: "—", tone: "stone" };
    const totalOrdered = items.reduce((s, i) => s + Number(i.quantity || 0), 0);
    const totalDelivered = items.reduce((s, i) => s + Number(i.delivered || 0), 0);
    if (totalOrdered === 0) return { label: "—", tone: "stone" };
    const pct = (totalDelivered / totalOrdered) * 100;
    if (pct >= 100) return { label: "Selesai", tone: "emerald", pct: 100 };
    if (pct === 0) return { label: "Belum Kirim", tone: "stone", pct: 0 };
    return { label: `${pct.toFixed(0)}% Kirim`, tone: "amber", pct };
  };

  return (
    <div className="min-h-full bg-[#fafaf5]">
    <div className="max-w-[1200px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <Link to="/client" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-stone-500 hover:text-blue-700">
        <ChevronLeft className="h-3 w-3" /> Beranda
      </Link>
      <PageHeader
        overline={`Klien · ${user?.client_name || "—"}`}
        title="PO & Rekap Kuota"
        description="Daftar Purchase Order atas nama perusahaan Anda beserta progres pengirimannya."
        action={(
          <Button onClick={openNewPo} data-testid="client-create-po-btn">
            <Plus className="h-4 w-4" /> Buat PO Baru
          </Button>
        )}
      />

      {/* Iter v34bv (#11) — Search + Filter */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari No. PO / material / catatan…" className="pl-9" data-testid="client-po-search" />
          </div>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="min-w-[160px]" data-testid="client-po-status-filter">
            <option value="all">Semua Status</option>
            <option value="open">Aktif</option>
            <option value="pending_approval">Menunggu Approval</option>
            <option value="pending_edit">Edit Menunggu</option>
            <option value="pending_delete">Hapus Menunggu</option>
            <option value="rejected">Ditolak</option>
          </Select>
          <Select value={quotaFilter} onChange={(e) => setQuotaFilter(e.target.value)} className="min-w-[160px]" data-testid="client-po-quota-filter">
            <option value="all">Semua Kuota</option>
            <option value="open">Belum Selesai</option>
            <option value="done">Kuota Selesai</option>
          </Select>
          {(search || statusFilter !== "all" || quotaFilter !== "all") && (
            <Button variant="ghost" onClick={() => { setSearch(""); setStatusFilter("all"); setQuotaFilter("all"); }} data-testid="client-po-reset-filter">Reset</Button>
          )}
          <span className="text-xs text-stone-500">Menampilkan {filtered.length} dari {rows.length}</span>
        </div>
      </Card>

      <Card className="p-0" data-testid="client-pos-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50/60 text-[10px] uppercase tracking-widest text-stone-500">
              <tr>
                <th className="text-left px-4 py-3">No. PO</th>
                <th className="text-left px-4 py-3">Tanggal</th>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-right px-4 py-3">Nilai</th>
                <th className="text-center px-4 py-3">SJ Terkait</th>
                <th className="text-center px-4 py-3">Status Kuota</th>
                <th className="px-2 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-stone-400">Memuat data…</td></tr>
              )}
              {!loading && pagi.pageItems.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-stone-400">{rows.length === 0 ? "Belum ada PO untuk perusahaan Anda." : "Tidak ada PO yang cocok dengan filter."}</td></tr>
              )}
              {!loading && pagi.pageItems.map((po) => {
                const qs = computeQuotaStatus(po);
                const st = STATUS_LABEL[po.status];
                const canEdit = ["open", "pending_approval", "rejected"].includes(po.status);
                const canDelete = ["open", "pending_approval"].includes(po.status);
                return (
                  <tr key={po.id} className="hover:bg-stone-50/40" data-testid={`client-po-row-${po.id}`}>
                    <td className="px-4 py-3 font-mono text-stone-900 text-xs cursor-pointer" onClick={() => setActivePo(po)}>
                      <div>{po.po_number}</div>
                      {st && st.tone !== "emerald" && (
                        <Badge tone={st.tone} className="mt-1 text-[9px]" data-testid={`client-po-status-${po.id}`}>{st.txt}</Badge>
                      )}
                      {po.rejection_reason && (
                        <div className="text-[9px] text-red-700 mt-0.5 italic max-w-[180px]">Alasan: {po.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-600 cursor-pointer" onClick={() => setActivePo(po)}>{fmtDate(po.contract_date)}</td>
                    <td className="px-4 py-3 text-stone-700 cursor-pointer" onClick={() => setActivePo(po)}>{(po.items || []).length} item</td>
                    <td className="px-4 py-3 text-right font-mono text-stone-900 cursor-pointer" onClick={() => setActivePo(po)}>{formatIDR(po.grand_total || 0)}</td>
                    <td className="px-4 py-3 text-center text-stone-600 cursor-pointer" onClick={() => setActivePo(po)}>{po.sj_count || 0}</td>
                    <td className="px-4 py-3 text-center cursor-pointer" onClick={() => setActivePo(po)}>
                      <Badge tone={qs.tone}>{qs.label}</Badge>
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap">
                      {canEdit && (
                        <button onClick={(e) => { e.stopPropagation(); openEdit(po); }} className="text-stone-500 hover:text-terra-600 px-1.5" title={po.status === "rejected" ? "Ajukan ulang" : "Edit"} data-testid={`client-po-edit-${po.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={(e) => { e.stopPropagation(); requestDelete(po); }} className="text-stone-500 hover:text-red-700 px-1.5" title={po.status === "pending_approval" ? "Batalkan" : "Ajukan hapus"} data-testid={`client-po-delete-${po.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagi.Pagination}
      </Card>

      {activePo && (
        <Modal open={!!activePo} onClose={() => setActivePo(null)} title={`Detail PO · ${activePo.po_number}`} size="lg" data-testid="client-po-detail-modal">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="uppercase tracking-widest text-stone-500 mb-1">Tanggal Kontrak</div>
                <div className="font-medium text-stone-900">{fmtDate(activePo.contract_date)}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-stone-500 mb-1">Nilai Total</div>
                <div className="font-mono text-stone-900">{formatIDR(activePo.grand_total || 0)}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-stone-500 mb-1">SJ Terkait</div>
                <div className="text-stone-900 flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-emerald-600" /> {activePo.sj_count || 0} pengiriman</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-stone-500 mb-1">Kategori</div>
                <div className="text-stone-900">{activePo.category || "—"}</div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-600 mb-2 flex items-center gap-1"><FileText className="h-3 w-3" /> Rekap Kuota per Item</div>
              <div className="overflow-x-auto border border-stone-200">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
                    <tr>
                      <th className="text-left px-3 py-2">Material</th>
                      <th className="text-left px-3 py-2">Satuan</th>
                      <th className="text-right px-3 py-2">Dipesan</th>
                      <th className="text-right px-3 py-2">Sudah Kirim</th>
                      <th className="text-right px-3 py-2">Sisa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {(activePo.items || []).map((it, i) => {
                      const remaining = Number(it.remaining || 0);
                      const pct = it.quantity > 0 ? (Number(it.delivered || 0) / Number(it.quantity)) * 100 : 0;
                      return (
                        <tr key={i}>
                          <td className="px-3 py-2 text-stone-900">{it.material_name}</td>
                          <td className="px-3 py-2 text-stone-600">{it.unit}</td>
                          <td className="px-3 py-2 text-right font-mono">{formatNumber(it.quantity || 0)}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">{formatNumber(it.delivered || 0)}</td>
                          <td className={`px-3 py-2 text-right font-mono ${remaining > 0 ? "text-amber-700" : "text-stone-400"}`}>
                            {formatNumber(remaining)}
                            <span className="text-[9px] uppercase ml-1 text-stone-400">({pct.toFixed(0)}%)</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {activePo.latest_sj?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-600 mb-2 flex items-center gap-1"><Truck className="h-3 w-3" /> SJ Terbaru</div>
                <div className="text-xs space-y-1">
                  {activePo.latest_sj.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 border border-stone-200 bg-stone-50/30">
                      <span className="font-mono text-stone-900">{s.sj_number}</span>
                      <span className="text-stone-500">{fmtDate(s.date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Iter v34x (#8) — Klien dapat lihat & download file PO yang sudah diupload. */}
            {activePo.file_id && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-600 mb-2 flex items-center gap-1"><FileText className="h-3 w-3" /> File PO Terlampir</div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${activePo.file_id}`}
                  target="_blank"
                  rel="noreferrer"
                  download={activePo.file_name || "po-file"}
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-stone-300 hover:bg-stone-50"
                  data-testid="client-po-file-download"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span className="font-mono">{activePo.file_name || "Unduh file PO"}</span>
                  {activePo.file_content_type && (
                    <span className="text-[10px] uppercase text-stone-400">({activePo.file_content_type.split("/")[1] || activePo.file_content_type})</span>
                  )}
                </a>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Iter v34v+w (#1/#2) — Modal buat/edit PO (klien-only). Edit & hapus butuh acc admin. */}
      <Modal open={openCreate} onClose={() => { setOpenCreate(false); setEditId(null); }} title={editId ? "Edit / Ajukan Ulang PO" : "Buat PO Baru"} size="xl">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="No. PO" required>
              <Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="PO-2026-001" data-testid="client-po-number" />
            </Field>
            <Field label="Tanggal Kontrak" required>
              <DatePicker value={form.contract_date} onChange={(e) => setForm({ ...form, contract_date: e.target.value })} data-testid="client-po-date" />
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-600">Item PO</label>
              <Button variant="secondary" className="!py-1 !px-2 text-xs" onClick={addItem} data-testid="client-po-add-item">
                <Plus className="h-3 w-3" /> Tambah Item
              </Button>
            </div>
            <div className="border border-stone-200">
              <div className="gap-1.5 p-2 bg-stone-50 text-[10px] uppercase tracking-wider font-semibold text-stone-600 border-b border-stone-200" style={{ display: "grid", gridTemplateColumns: "3fr 1.2fr 1.5fr 2fr 0.6fr" }}>
                <div>Nama Material</div>
                <div>Satuan</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Harga Satuan</div>
                <div></div>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="gap-1.5 p-2 border-b border-stone-100 items-center" style={{ display: "grid", gridTemplateColumns: "3fr 1.2fr 1.5fr 2fr 0.6fr" }}>
                  <Input value={it.material_name} onChange={(e) => setItem(i, "material_name", e.target.value)} placeholder="Nama material" data-testid={`client-po-item-name-${i}`} />
                  <Select value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} data-testid={`client-po-item-unit-${i}`}>
                    <option value="">—</option>
                    {units.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                    {it.unit && !units.find((u) => u.name === it.unit) && <option value={it.unit}>{it.unit}</option>}
                  </Select>
                  <Input className="font-mono-num text-right" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="0" data-testid={`client-po-item-qty-${i}`} />
                  <Input className="font-mono-num text-right" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} placeholder="0" data-testid={`client-po-item-price-${i}`} />
                  <button onClick={() => rmItem(i)} className="text-stone-400 hover:text-red-700 justify-self-center" data-testid={`client-po-item-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="File PO (JPG/PNG/PDF — opsional)">
              <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-stone-300 hover:bg-stone-50 cursor-pointer">
                <Upload className="h-3.5 w-3.5" />
                <span>{uploading ? "Mengunggah…" : (form.file_id ? `Ganti file (${form.file_name})` : "Pilih file")}</span>
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={uploadFile} data-testid="client-po-file-input" />
              </label>
            </Field>
            <Field label="Catatan">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Catatan tambahan…" data-testid="client-po-notes" />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpenCreate(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid="client-po-save-btn">
              {saving ? "Menyimpan…" : (editId ? "Ajukan Perubahan" : "Buat PO")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
    </div>
  );
}
